import axios, { type AxiosInstance } from "axios"
import config from "config"
import type { ICreateLicense, ILicense, IUpdateLicense } from "types/licenseTypes"

import authService from "./authService"

class LicenseService {
  private http: AxiosInstance

  public constructor() {
    this.http = axios.create({
      baseURL: config.apiUrl,
      headers: {
        "Content-type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    })
  }

  public createLicense = async (createLicense: ICreateLicense): Promise<Array<ILicense>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.post<Array<ILicense>>("/license", createLicense, config)

    return data
  }

  public getLicenses = async (): Promise<Array<ILicense>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<ILicense>>("/license", config)

    return data
  }

  public getLicense = async (id: string): Promise<ILicense> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<ILicense>(`/license/${id}`, config)

    return data
  }

  public updateLicense = async (
    licenseId: string,
    updateLicense: IUpdateLicense,
  ): Promise<ILicense> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.put<ILicense>(`/license/${licenseId}`, updateLicense, config)

    return data
  }

  public deleteLicense = async (licenseId: string) => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.delete(`/license/${licenseId}`, config)

    return resp
  }

  public cancelLicense = async (licenseId: string): Promise<ILicense> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.post<ILicense>(
      `/license/${licenseId}/cancel`,
      undefined,
      config,
    )

    return data
  }

  public getLicensePoolLicenses = async (licensePoolId: string): Promise<Array<ILicense>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<ILicense>>(
      `/licensepool/${licensePoolId}/license`,
      config,
    )

    return data
  }

  public getOrganizationLicenses = async (organizationId: string): Promise<Array<ILicense>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<ILicense>>(
      `/organization/${organizationId}/license`,
      config,
    )

    return data
  }
}

const licenseService = new LicenseService()

export default licenseService
